class ShippingRates extends HTMLElement {
    constructor() {
        super();

        this.form = null;
        this.ratesList = null;
        this.zipCode = null;
        this.button = null;
        this.message = null;
        this.submitButton = null;
        this.location = this.getAttribute('data-location');
        this.initialized = false;
        this.pendingInitFrame = null;
        this.initObserver = null;
        this.originalCartItems = [];
        this.invalidZipMessage = 'CEP inválido.';
        this.genericErrorMessage = 'Não conseguimos consultar, tente novamente ou entre em contato com nosso atendimento.';
        this.outOfAreaMessage = 'Ops, CEP fora da área de entrega. Confira se o CEP está preenchido corretamente ou entre em contato com nosso atendimento.';
    }

    debug = () => {}

    connectedCallback() {
        if (!this.refreshElements()) {
            this.scheduleInitializationRetry();
            this.startInitializationObserver();
            return;
        }

        if (this.pendingInitFrame !== null) {
            cancelAnimationFrame(this.pendingInitFrame);
            this.pendingInitFrame = null;
        }

        this.stopInitializationObserver();

        this.message.classList.remove('hidden');
        this.hideMessage();

        if (this.initialized) {
            return;
        }

        this.addEventListener('click', this.handleClick);
        this.addEventListener('input', this.handleInput);
        this.addEventListener('keydown', this.handleKeydown);
        this.addEventListener('focusin', this.handleFocusIn);

        this.initialized = true;
    }

    disconnectedCallback() {
        if (this.pendingInitFrame !== null) {
            cancelAnimationFrame(this.pendingInitFrame);
            this.pendingInitFrame = null;
        }

        this.stopInitializationObserver();

        if (!this.initialized) {
            return;
        }

        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('input', this.handleInput);
        this.removeEventListener('keydown', this.handleKeydown);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.initialized = false;
    }

    scheduleInitializationRetry = () => {
        if (this.pendingInitFrame !== null) {
            return;
        }

        this.pendingInitFrame = requestAnimationFrame(() => {
            this.pendingInitFrame = null;

            if (!this.isConnected) {
                return;
            }

            this.connectedCallback();
        });
    }

    startInitializationObserver = () => {
        if (this.initObserver) {
            return;
        }

        this.initObserver = new MutationObserver(() => {
            if (!this.refreshElements()) {
                return;
            }

            this.connectedCallback();
        });

        this.initObserver.observe(this, {
            childList: true,
            subtree: true
        });
    }

    stopInitializationObserver = () => {
        if (!this.initObserver) {
            return;
        }

        this.initObserver.disconnect();
        this.initObserver = null;
    }

    refreshElements = () => {
        this.form = this.querySelector('[data-form="shipping-rates"]');
        this.ratesList = this.querySelector('[data-shipping-rates]');
        this.zipCode = this.querySelector('[data-zip-code]');
        this.button = this.querySelector('[data-form-show]');
        this.message = this.querySelector('[data-message]');
        this.zipCodeInput = this.querySelector('#zip-code');
        this.submitButton = this.querySelector('.btn-submit');

        const hasAllElements = Boolean(
            this.form
            && this.ratesList
            && this.zipCode
            && this.button
            && this.message
            && this.zipCodeInput
            && this.submitButton
        );

        return hasAllElements;
    }

    reset = () => {
        if (!this.refreshElements()) {
            return;
        }

        this.showElement(this.form);
        this.hideElement(this.ratesList);
        this.hideElement(this.zipCode);
        this.hideMessage();
    }

    handleInputInteraction = () => {
        if (!this.refreshElements()) {
            return;
        }

        this.hideMessage();
    }

    handleInputKeydown = (event) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.submit();
    }

    handleSubmitAction = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.submit();
    }

    handleClick = (event) => {
        if (event.target.closest('.btn-submit')) {
            this.handleSubmitAction(event);
            return;
        }

        if (event.target.closest('[data-form-show]')) {
            this.reset();
            return;
        }

        if (event.target.closest('#zip-code')) {
            this.handleInputInteraction();
        }
    }

    handleInput = (event) => {
        if (!event.target.matches('#zip-code')) {
            return;
        }

        this.maskZipCode(event);
    }

    handleKeydown = (event) => {
        if (!event.target.matches('#zip-code')) {
            return;
        }

        this.handleInputKeydown(event);
    }

    handleFocusIn = (event) => {
        if (!event.target.matches('#zip-code')) {
            return;
        }

        this.handleInputInteraction();
    }

    submit = async () => {
        if (!this.refreshElements()) {
            return;
        }

        const buttonSubmit = this.submitButton;

        this.hideMessage();
        const zip = this.zipCodeInput?.value?.replace(/\D/g, '') || '';

        if (zip.length !== 8) {
            this.displayError(this.invalidZipMessage);
            return;
        }

        if (buttonSubmit) {
            buttonSubmit.setAttribute('disabled', 'true');
            buttonSubmit.innerHTML = 'Consultando...';
        }

        try {
            const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${zip}`);

            if (!response.ok) {
                if ([400, 404, 422, 504].includes(response.status)) {
                    throw new Error(this.invalidZipMessage);
                }

                throw new Error(this.genericErrorMessage);
            }

            const { state, cep } = await response.json();

            if (!state || !cep) {
                throw new Error(this.invalidZipMessage);
            }

            const shippingRates = await this.fetchShippingRates(state, cep);

            this.displayShippingRates(shippingRates);
            this.updateButton(cep);

            this.sendDataLayer({
                event: 'shipping_calculate',
                category: 'shipping',
                zipCode: cep,
                shippingRates: shippingRates.shipping_rates.map((shipping) => ({
                    label: shipping.name,
                    value: shipping.price,
                    currency: shipping.currency,
                    deadline: shipping.delivery_days?.[0] || null,
                    source: shipping.source,
                })),
                pageType: this.location,
                url: window.location.href
            });
        } catch (error) {
            this.displayError(error.message || this.genericErrorMessage);
        } finally {
            if (buttonSubmit) {
                buttonSubmit.removeAttribute('disabled');
                buttonSubmit.innerHTML = 'Consultar';
            }
        }
    }

    getSelectedVariantId() {
        return document.querySelector('form[action="/cart/add"] input[name="id"]')?.value
            || document.querySelector('input[name="id"]')?.value
            || null;
    }

    async saveCurrentCart() {
        const response = await fetch('/cart.js');
        const data = await response.json();
        this.originalCartItems = data.items || [];
    }

    async clearCart() {
        await fetch('/cart/clear.js', { method: 'POST' });
    }

    async restoreOriginalCart() {
        await this.clearCart();

        if (!this.originalCartItems.length) {
            return;
        }

        const items = this.originalCartItems.map((item) => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
        }));

        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items })
        });
    }

    async simulationCart(variantId, quantity = 1) {
        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                items: [{
                    id: variantId,
                    quantity
                }]
            })
        });
    }

    cartMatchesSimulation(variantId, quantity) {
        if (this.originalCartItems.length !== 1) {
            return false;
        }

        const item = this.originalCartItems[0];
        const hasProperties = item.properties && Object.keys(item.properties).length > 0;

        return String(item.variant_id) === String(variantId)
            && item.quantity === quantity
            && !hasProperties;
    }

    updateButton(zip) {
        if (!this.refreshElements()) {
            return;
        }

        const container = this.zipCode;
        container.querySelector('span').textContent = zip;
        this.showElement(this.zipCode);
    }

    displayShippingRates(shippingRates) {
        if (!this.refreshElements()) {
            return;
        }

        if (!shippingRates?.shipping_rates?.length) {
            throw new Error(this.outOfAreaMessage);
        }

        const fragment = document.createDocumentFragment();
        shippingRates.shipping_rates.forEach((rate) => fragment.appendChild(this.createRateElement(rate)));

        this.ratesList.innerHTML = '';
        this.ratesList.appendChild(fragment);
        this.showElement(this.ratesList);
        this.hideElement(this.form);
    }

    createRateElement(rate) {
        const rateContainer = this.createElementWithClass('li', 'list-shipping-rates__item');
        const rateTitle = this.createElementWithClass('span', 'rate__title', rate.name);
        const hasDeadline = Boolean(rate.delivery_days?.[0]);
        const priceValue = this.parsePrice(rate.price);
        const isPaid = priceValue > 0;
        const deadlineText = hasDeadline
            ? `Em até ${rate.delivery_days[0]} dias úteis`
            : 'Confira a disponibilidade no checkout';
        const priceText = isPaid ? `R$ ${this.formatPrice(priceValue)}` : 'Frete grátis';
        const rateText = isPaid ? `${deadlineText} - ${priceText}` : priceText;
        const rateContent = this.createElementWithClass('span', 'rate__content', rateText);

        rateContainer.append(rateTitle, rateContent);

        return rateContainer;
    }

    formatPrice(value) {
        return this.parsePrice(value).toFixed(2).replace('.', ',');
    }

    parsePrice(value) {
        const normalized = String(value ?? '')
            .trim()
            .replace(/[^\d,.-]/g, '')
            .replace(',', '.');
        const parsed = Number(normalized);

        return Number.isFinite(parsed) ? parsed : 0;
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async fetchShippingRates(province, zip) {
        const queryParams = new URLSearchParams({
            'shipping_address[zip]': zip,
            'shipping_address[country]': 'BR',
            'shipping_address[province]': province
        });
        let lastError = new Error(this.genericErrorMessage);

        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const response = await fetch(`/cart/shipping_rates.json?${queryParams.toString()}`);

                if (!response.ok || response.status === 204) {
                    if (response.status === 422) {
                        throw new Error(this.outOfAreaMessage);
                    }

                    throw new Error(this.genericErrorMessage);
                }

                const data = await response.json();

                if (!data?.shipping_rates?.length) {
                    throw new Error(this.outOfAreaMessage);
                }

                return data;
            } catch (error) {
                if (error.message === this.outOfAreaMessage) {
                    throw error;
                }

                lastError = error;

                if (attempt === 2) {
                    throw lastError;
                }

                await this.delay(400);
            }
        }

        throw lastError;
    }

    createElementWithClass(tag, className, textContent = '') {
        const element = document.createElement(tag);
        element.classList.add(className);
        element.textContent = textContent;

        return element;
    }

    showElement(element) {
        element.classList.remove('hidden');
    }

    hideElement(element) {
        element.classList.add('hidden');
    }

    maskZipCode = (event) => {
        const input = event.currentTarget;

        if (input.value.length >= 9) {
            return;
        }

        let cep = input.value.replace(/\D/g, '');

        if (cep.length > 5) {
            cep = cep.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
        }

        input.value = cep;
    }

    displayError(message) {
        if (!this.refreshElements()) {
            return;
        }

        this.message.textContent = message;
        this.showMessage();
    }

    showMessage() {
        if (!this.message) {
            return;
        }

        this.message.classList.add('is-visible');
        this.message.setAttribute('aria-hidden', 'false');
    }

    hideMessage() {
        if (!this.message) {
            return;
        }

        this.message.classList.remove('is-visible');
        this.message.setAttribute('aria-hidden', 'true');
    }

    sendDataLayer(data) {
        try {
            Shopify.analytics.publish(PUB_SUB_EVENTS.shippingCalculate, data);
        } catch (error) {
            console.error('Erro ao enviar dados para o Data Layer:', error);
        }
    }
}

if (!customElements.get('shipping-rates')) {
    customElements.define('shipping-rates', ShippingRates);
}
